import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { api, downloadUrl } from "@/lib/api";
import { downloadCsv } from "@/lib/csvDownload";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Upload,
  FileText,
  Trash2,
  RefreshCw,
  Inbox,
  Eye,
  Calculator,
  Link as LinkIcon,
  Download,
  PlugZap,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import DateRangeFilter, {
  presetRange,
  ymd,
} from "@/components/DateRangeFilter";
import { fmtMoney } from "@/lib/intl";

function StatCard({ label, value, hint, testId }) {
  return (
    <Card
      data-testid={testId}
      className="p-6 rounded-lg border border-zinc-200 shadow-none bg-white"
    >
      <div className="text-2xs sm:text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
        {label}
      </div>
      <div className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight text-zinc-950 font-mono-num">
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-zinc-500">{hint}</div>}
    </Card>
  );
}

export default function Payslips() {
  const { status } = useOutletContext();
  const [slips, setSlips] = useState([]);
  // bonus map keyed by payslip._id ; values: {bonus, bonus_raw, below_threshold, calculated_at}
  const [bonusBySlip, setBonusBySlip] = useState({});
  const [employees, setEmployees] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selected, setSelected] = useState(null);
  const fileInput = useRef(null);

  // Bonus calculator state
  const [bonusEmployee, setBonusEmployee] = useState("");
  const [bonusRate, setBonusRate] = useState("0.50");
  const [gstRate, setGstRate] = useState("0.10");
  const [minPayment, setMinPayment] = useState("20");
  const [bonusPreset, setBonusPreset] = useState("last_quarter");
  const [bonusRange, setBonusRange] = useState(() => presetRange("last_quarter"));
  const [bonusResult, setBonusResult] = useState(null);
  const [bonusLoading, setBonusLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // Xero sync state
  const [xeroStatus, setXeroStatus] = useState(null);
  const [xeroSyncing, setXeroSyncing] = useState(false);

  // Duplicate-payslips banner state
  const [duplicates, setDuplicates] = useState(null);      // {groups:[], total_groups:N}
  const [cleaningDupes, setCleaningDupes] = useState(false);

  async function loadDuplicates() {
    try {
      const r = await api.get("/payslips/duplicates");
      setDuplicates(r.data);
    } catch (_) {
      setDuplicates(null);
    }
  }

  async function cleanupDuplicates() {
    if (!window.confirm(
      "Delete duplicate payslip rows? The most-recently uploaded row for each " +
      "(employee, pay period) is kept; the older duplicates are removed.\n\n" +
      "This is safe — the original PDF is untouched, only the parsed rows " +
      "are cleaned up. Proceed?"
    )) return;
    setCleaningDupes(true);
    try {
      const r = await api.post("/payslips/duplicates/cleanup?dry_run=false");
      toast.success(`Removed ${r.data.deleted} duplicate payslip rows`, {
        description: `${r.data.groups} group(s) reconciled`,
      });
      await Promise.all([refresh(), loadDuplicates()]);
    } catch (e) {
      toast.error("Cleanup failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setCleaningDupes(false);
    }
  }

  async function loadXeroStatus() {
    try {
      const r = await api.get("/xero/status");
      setXeroStatus(r.data);
    } catch (e) {
      // status is best-effort; ignore
    }
  }

  async function syncFromXero() {
    setXeroSyncing(true);
    try {
      const r = await api.post("/xero/sync", null, { timeout: 180000 });
      const { upserted, pay_runs_seen, errors } = r.data || {};
      toast.success(`Xero sync complete`, {
        description: `${upserted} payslips · ${pay_runs_seen} pay runs${
          errors?.length ? ` · ${errors.length} errors` : ""
        }`,
      });
      await Promise.all([refresh(), loadXeroStatus()]);
    } catch (e) {
      toast.error("Xero sync failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setXeroSyncing(false);
    }
  }

  async function refresh() {
    setLoading(true);
    try {
      const [slipsRes, empRes, cfgRes] = await Promise.all([
        api.get("/payslips"),
        api.get("/payslips/employees"),
        api.get("/bonus-config"),
      ]);
      setSlips(slipsRes.data || []);
      setEmployees(empRes.data || []);
      if (cfgRes.data) {
        setBonusRate(String(cfgRes.data.bonus_rate ?? 0.50));
        setGstRate(String(cfgRes.data.gst_rate ?? 0.10));
        setMinPayment(String(cfgRes.data.min_payment ?? 20));
      }
      if (status?.configured) {
        try {
          const cal = await api.get("/calendars");
          setCalendars(cal.data || []);
        } catch (e) { /* noop */ }
      }
    } catch (e) {
      toast.error("Failed to load payslips", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      await refresh();
      await Promise.all([loadXeroStatus(), loadDuplicates()]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bulk lookup of saved bonuses for the visible slips (powers the
  // "Bonus $X" badge in the table). Re-runs whenever slip IDs change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slips.length) {
        if (!cancelled) setBonusBySlip({});
        return;
      }
      try {
        const ids = slips.map((s) => s.id).filter(Boolean).slice(0, 500);
        const r = await api.post("/payslips/bonus/by-slips", { payslip_ids: ids });
        if (!cancelled) setBonusBySlip(r.data || {});
      } catch (_) {
        if (!cancelled) setBonusBySlip({});
      }
    })();
    return () => { cancelled = true; };
  }, [slips]);

  async function uploadFile(file, { importAnyway = false } = {}) {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please drop a PDF file");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (importAnyway) fd.append("import_anyway", "true");
    try {
      const r = await api.post("/payslips/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 240000,
      });
      const {
        parsed_count, failed_count, duplicate_count,
        duplicate_pdf, duplicate_reason,
      } = r.data || {};
      if (duplicate_pdf) {
        // Whole file matched a previous upload — offer to force.
        const force = window.confirm(
          `${duplicate_reason || "This PDF has already been imported."}\n\n`
          + "OK = import anyway (creates a fresh copy)\n"
          + "Cancel = skip",
        );
        if (force) {
          setUploading(false);
          return uploadFile(file, { importAnyway: true });
        }
        toast.info(`Skipped ${file.name}`, {
          description: `Already imported (${parsed_count} slips on file).`,
        });
      } else {
        const bits = [`Parsed ${parsed_count} payslips`];
        if (duplicate_count) bits.push(`${duplicate_count} duplicate${duplicate_count === 1 ? "" : "s"} skipped`);
        if (failed_count) bits.push(`${failed_count} failed`);
        toast.success(`Uploaded ${file.name}`, { description: bits.join(", ") + "." });
      }
      await refresh();
    } catch (e) {
      toast.error("Upload failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setUploading(false);
    }
  }

  async function deleteSlip(id) {
    try {
      await api.delete(`/payslips/${id}`);
      toast.success("Payslip deleted");
      await refresh();
    } catch (e) {
      toast.error("Delete failed", { description: e.message });
    }
  }

  async function linkEmployeeCalendar(employeeName, calendarId) {
    try {
      await api.post(
        `/payslips/employee/${encodeURIComponent(employeeName)}/link-calendar?calendar_id=${calendarId || ""}`
      );
      toast.success("Calendar linked", {
        description: `${employeeName} → calendar ${calendarId || "(cleared)"}`,
      });
      await refresh();
    } catch (e) {
      toast.error("Link failed", { description: e.message });
    }
  }

  async function exportBonusCsv() {
    if (!bonusResult) return;
    const safeName = (bonusResult.employee_name || "employee").replace(/[^A-Za-z0-9_-]+/g, "_");
    const filename = `bonus_appointments_${safeName}_${bonusResult.window?.minDate}_${bonusResult.window?.maxDate}.csv`;
    try {
      await downloadCsv(
        "/payslips/bonus/csv",
        {
          employee_name: bonusResult.employee_name,
          minDate: bonusResult.window?.minDate,
          maxDate: bonusResult.window?.maxDate,
        },
        filename,
      );
    } catch (e) {
      toast.error("CSV download failed", { description: e.message });
    }
  }

  async function calculateBonus() {
    if (!bonusEmployee) {
      toast.error("Pick an employee");
      return;
    }
    setBonusLoading(true);
    setBonusResult(null);
    try {
      const r = await api.get("/payslips/bonus", {
        params: {
          employee_name: bonusEmployee,
          minDate: ymd(bonusRange[0]),
          maxDate: ymd(bonusRange[1]),
          rate: Number(bonusRate),
          gst_rate: Number(gstRate),
          min_payment: Number(minPayment),
        },
        timeout: 60000,
      });
      setBonusResult(r.data);
      // Refresh badges in the slips table so the just-calculated bonus
      // shows up inline immediately.
      try {
        const ids = slips.map((s) => s.id).filter(Boolean).slice(0, 500);
        if (ids.length) {
          const bl = await api.post("/payslips/bonus/by-slips", { payslip_ids: ids });
          setBonusBySlip(bl.data || {});
        }
      } catch (_) { /* badge refresh is non-critical */ }
    } catch (e) {
      toast.error("Bonus calc failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBonusLoading(false);
    }
  }

  async function saveBonusConfig() {
    setSavingConfig(true);
    try {
      await api.post("/bonus-config", {
        bonus_rate: Number(bonusRate),
        gst_rate: Number(gstRate),
        min_payment: Number(minPayment),
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSavingConfig(false);
    }
  }

  const totalGross = useMemo(
    () => slips.reduce((a, s) => a + Number(s.gross_pay || 0), 0),
    [slips]
  );
  const totalHours = useMemo(
    () => slips.reduce((a, s) => a + Number(s.hours_total || 0), 0),
    [slips]
  );

  return (
    <div className="space-y-8" data-testid="payslips-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Payroll
          </div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
            Payroll Bonus
          </h1>
          <p className="mt-2 text-sm text-zinc-600 max-w-xl leading-relaxed">
            Upload payslip PDFs (one or many slips per file). The data is
            parsed, stored permanently, and used to calculate bonus payments
            against your Acuity calendar revenue.
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          {xeroStatus?.connected && (
            <Button
              onClick={syncFromXero}
              disabled={xeroSyncing}
              data-testid="payslips-xero-sync-btn"
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
            >
              <PlugZap className={cn("h-4 w-4 mr-1.5", xeroSyncing && "animate-pulse")} />
              {xeroSyncing
                ? "Syncing…"
                : `Sync from Xero${
                    xeroStatus?.tenant_name ? ` · ${xeroStatus.tenant_name}` : ""
                  }`}
            </Button>
          )}
          <Button
            onClick={refresh}
            variant="outline"
            className="border-zinc-300 hover:bg-zinc-100"
            data-testid="payslips-refresh-btn"
          >
            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {duplicates?.total_groups > 0 && (
        <Card
          data-testid="payslips-duplicates-banner"
          className="p-4 rounded-md border border-amber-300 shadow-none bg-amber-50 flex flex-col gap-3"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Trash2 className="h-4 w-4 text-amber-700 shrink-0" />
            <div className="text-sm text-amber-900 flex-1">
              <b>Duplicate payslips detected.</b>{" "}
              {duplicates.total_groups} pay period{duplicates.total_groups === 1 ? "" : "s"}{" "}
              {duplicates.total_groups === 1 ? "has" : "have"} been uploaded more than once,
              which was doubling the totals in the Bonus Calculator. The most-recent upload
              per period will be kept.
            </div>
            <Button
              size="sm"
              onClick={cleanupDuplicates}
              disabled={cleaningDupes}
              className="bg-amber-700 hover:bg-amber-800 text-white shrink-0"
              data-testid="payslips-duplicates-cleanup-btn"
            >
              {cleaningDupes ? "Cleaning…" : "Remove duplicates"}
            </Button>
          </div>
          <div className="rounded border border-amber-200 bg-white/60 overflow-hidden"
            data-testid="payslips-duplicates-list">
            <table className="w-full text-xs">
              <thead className="bg-amber-100/70 text-amber-900">
                <tr>
                  <th className="text-left px-3 py-1.5">Employee</th>
                  <th className="text-left px-3 py-1.5">Pay period</th>
                  <th className="text-right px-3 py-1.5">Copies</th>
                  <th className="text-right px-3 py-1.5">Will remove</th>
                </tr>
              </thead>
              <tbody>
                {duplicates.groups.map((g) => {
                  const key = `${g.employee_name}|${g.pay_period_start}|${g.pay_period_end}`;
                  return (
                    <tr key={key} className="border-t border-amber-100"
                      data-testid={`payslips-duplicate-row-${key}`}>
                      <td className="px-3 py-1.5 font-medium text-zinc-900">
                        {g.employee_name}
                      </td>
                      <td className="px-3 py-1.5 font-mono-num text-zinc-700">
                        {g.pay_period_start} → {g.pay_period_end}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono-num">{g.count}</td>
                      <td className="px-3 py-1.5 text-right font-mono-num text-amber-800">
                        {(g.redundant_ids || []).length}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!xeroStatus?.connected && (
        <Card className="p-4 rounded-md border border-zinc-200 shadow-none bg-zinc-50 flex items-center gap-3">
          <PlugZap className="h-4 w-4 text-zinc-500 shrink-0" />
          <div className="text-sm text-zinc-700 flex-1">
            Want payslips synced automatically?{" "}
            <Link to="/settings" className="underline">
              Connect Xero Payroll
            </Link>{" "}
            to skip the PDF upload step.
          </div>
        </Card>
      )}

      {/* Upload */}
      <Card
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) uploadFile(f);
        }}
        className={cn(
          "p-8 sm:p-10 rounded-lg border-2 border-dashed transition-colors text-center",
          dragOver
            ? "border-zinc-950 bg-zinc-50"
            : "border-zinc-200 bg-zinc-50/40 hover:bg-zinc-50"
        )}
        data-testid="payslip-dropzone"
      >
        <div className="mx-auto h-12 w-12 grid place-items-center rounded-md bg-zinc-950 text-white mb-4">
          <Upload className="h-6 w-6" strokeWidth={1.75} />
        </div>
        <h3 className="font-display text-2xl font-semibold tracking-tight text-zinc-950">
          {uploading ? "Parsing payslips…" : "Drop a PDF to upload"}
        </h3>
        <p className="mt-2 text-sm text-zinc-600 max-w-md mx-auto">
          Multi-page PDFs are supported — one payslip per page is parsed and
          stored. Auto-linked to your Acuity calendars by name.
        </p>
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => uploadFile(e.target.files?.[0])}
          data-testid="payslip-file-input"
        />
        <Button
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          className="mt-5 bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="payslip-browse-btn"
        >
          {uploading ? "Uploading…" : "Browse files"}
        </Button>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Slips stored" value={slips.length} testId="stat-slips" />
        <StatCard label="Employees" value={employees.length} testId="stat-employees" />
        <StatCard
          label="Total gross"
          value={fmtMoney(totalGross)}
          testId="stat-total-gross"
        />
        <StatCard
          label="Total hours"
          value={totalHours.toFixed(1)}
          testId="stat-total-hours"
        />
      </div>

      {/* Bonus calculator */}
      <Card className="p-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <Calculator className="h-3.5 w-3.5" /> Bonus calculator
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={saveBonusConfig}
            disabled={savingConfig}
            data-testid="bonus-save-config-btn"
            className="border-zinc-300 hover:bg-zinc-100 h-8"
          >
            {savingConfig ? "Saving…" : "Save bonus % / GST"}
          </Button>
        </div>

        <p className="mt-2 text-xs text-zinc-600 leading-relaxed max-w-3xl">
          Per pay period:
          <span className="font-mono-num text-zinc-800">
            {" "}revenue<sub>ex&nbsp;GST</sub> − (pay + super) − ½&nbsp;revenue<sub>ex&nbsp;GST</sub>
          </span>
          {" "}= bonus opportunity. If positive, bonus = opportunity × bonus%.
        </p>

        <div className="mt-4 space-y-5">
          {/* Quick-pick: load filters from an existing payslip.
              Filtered by `bonusEmployee` when set — otherwise it's a huge
              cross-employee list which is rarely what the operator wants. */}
          {slips.length > 0 && (
            <div className="rounded-md border border-dashed border-zinc-300 bg-zinc-50/60 p-3" data-testid="bonus-payslip-picker-section">
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">
                Quick-pick from existing payslip
                {bonusEmployee && (
                  <span className="ml-2 normal-case tracking-normal text-zinc-500">
                    · filtered to <strong className="text-zinc-800">{bonusEmployee}</strong>
                  </span>
                )}
              </Label>
              <div className="mt-1.5 flex flex-col sm:flex-row gap-2">
                <Select
                  value=""
                  onValueChange={(slipId) => {
                    const slip = slips.find((s) => s.id === slipId);
                    if (!slip) return;
                    if (slip.employee_name) setBonusEmployee(slip.employee_name);
                    const ps = slip.pay_period_start;
                    const pe = slip.pay_period_end;
                    if (ps && pe) {
                      setBonusPreset("custom");
                      setBonusRange([new Date(`${ps}T00:00:00`), new Date(`${pe}T00:00:00`)]);
                      toast.success(`Loaded ${slip.employee_name}: ${ps} → ${pe}`);
                    }
                  }}
                >
                  <SelectTrigger
                    className="border-zinc-300 bg-white"
                    data-testid="bonus-payslip-picker"
                  >
                    <SelectValue
                      placeholder={
                        bonusEmployee
                          ? `— Choose a ${bonusEmployee} payslip to pre-fill the date range —`
                          : "— Choose a payslip to pre-fill employee + date range —"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-[320px]">
                    {(() => {
                      const matches = slips.filter(
                        (s) => s.employee_name && s.pay_period_start && s.pay_period_end
                          && (!bonusEmployee || s.employee_name === bonusEmployee),
                      );
                      if (matches.length === 0) {
                        return (
                          <div
                            className="px-3 py-4 text-xs text-zinc-500 text-center"
                            data-testid="bonus-payslip-picker-empty"
                          >
                            {bonusEmployee
                              ? `No payslips stored for ${bonusEmployee} yet.`
                              : "No payslips available."}
                          </div>
                        );
                      }
                      return matches.slice(0, 200).map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {/* When filtered to one employee, drop the name from
                              the label — the header already shows it. */}
                          {bonusEmployee
                            ? `${s.pay_period_start} → ${s.pay_period_end}`
                            : `${s.employee_name} · ${s.pay_period_start} → ${s.pay_period_end}`}
                          {s.gross_pay ? ` · gross $${Number(s.gross_pay).toFixed(0)}` : ""}
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <p className="mt-1.5 text-2xs text-zinc-500">
                {bonusEmployee
                  ? "Sets the Date range below for the chosen pay period — you can still tweak it before clicking Calculate."
                  : "Sets the Employee and Date range below; you can still tweak them before clicking Calculate."}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">
                Employee
              </Label>
              <Select value={bonusEmployee} onValueChange={setBonusEmployee}>
                <SelectTrigger
                  className="border-zinc-300 mt-1.5"
                  data-testid="bonus-employee-select"
                >
                  <SelectValue placeholder="Pick an employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.employee_name} value={e.employee_name}>
                      {e.employee_name}
                      {e.linked_calendar_id ? " · linked" : " · not linked"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">
                Bonus %
              </Label>
              <Input
                value={bonusRate}
                onChange={(e) => setBonusRate(e.target.value)}
                type="number"
                step="0.01"
                min="0"
                max="1"
                className="border-zinc-300 mt-1.5 font-mono-num"
                data-testid="bonus-rate-input"
              />
              <p className="text-xs text-zinc-500 mt-1">e.g. 0.10 = 10%</p>
            </div>
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">
                GST rate
              </Label>
              <Input
                value={gstRate}
                onChange={(e) => setGstRate(e.target.value)}
                type="number"
                step="0.01"
                min="0"
                max="1"
                className="border-zinc-300 mt-1.5 font-mono-num"
                data-testid="bonus-gst-input"
              />
              <p className="text-xs text-zinc-500 mt-1">AU GST = 0.10</p>
            </div>
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">
                Min payment
              </Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                <Input
                  value={minPayment}
                  onChange={(e) => setMinPayment(e.target.value)}
                  type="number"
                  step="1"
                  min="0"
                  className="border-zinc-300 font-mono-num pl-7"
                  data-testid="bonus-min-payment-input"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">Below this, bonus = $0</p>
            </div>
          </div>

          <DateRangeFilter
            preset={bonusPreset}
            setPreset={setBonusPreset}
            range={bonusRange}
            setRange={setBonusRange}
            idPrefix="bonus-range"
          />

          <Button
            onClick={calculateBonus}
            disabled={bonusLoading || !bonusEmployee}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="bonus-calculate-btn"
          >
            <Calculator
              className={cn("h-4 w-4 mr-1.5", bonusLoading && "animate-pulse")}
            />
            {bonusLoading ? "Calculating…" : "Calculate"}
          </Button>

          {!bonusResult && (
            <div
              className="rounded-md border border-zinc-200 bg-zinc-50 p-8 text-center text-sm text-zinc-500"
              data-testid="bonus-result-empty"
            >
              Pick an employee, range and bonus%, then press Calculate.
            </div>
          )}

          {bonusResult && bonusResult.periods !== undefined && (
            <div className="space-y-4" data-testid="bonus-result">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-zinc-600 font-mono-num">
                  {bonusResult.window?.minDate} → {bonusResult.window?.maxDate} ·{" "}
                  calendar #{bonusResult.calendar_id}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-zinc-300 hover:bg-zinc-100 h-8"
                  onClick={exportBonusCsv}
                  data-testid="bonus-export-csv"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Export appointments CSV
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="p-5 rounded-md border border-zinc-200 shadow-none bg-zinc-950 text-white">
                  <div className="text-2xs uppercase tracking-[0.2em] text-zinc-400">
                    Total bonus payable
                  </div>
                  <div className="font-mono-num text-4xl font-medium tracking-tight mt-2">
                    {fmtMoney(bonusResult.totals?.bonus)}
                  </div>
                  <div className="text-xs text-zinc-400 font-mono-num mt-2">
                    rate {bonusResult.rate} · GST {bonusResult.gst_rate} ·{" "}
                    min {fmtMoney(bonusResult.min_payment ?? 20)} ·{" "}
                    {bonusResult.periods?.length || 0} period
                    {(bonusResult.periods?.length || 0) === 1 ? "" : "s"}
                  </div>
                </Card>
                <Card className="p-5 rounded-md border border-zinc-200 shadow-none bg-white">
                  <div className="text-2xs uppercase tracking-[0.2em] text-zinc-500">
                    Revenue (ex GST)
                  </div>
                  <div className="font-mono-num text-2xl text-zinc-950 mt-2">
                    {fmtMoney(bonusResult.totals?.revenue_ex_gst)}
                  </div>
                  <div className="text-xs text-zinc-500 font-mono-num mt-1">
                    {fmtMoney(bonusResult.totals?.revenue_inc_gst)} inc GST ·{" "}
                    {bonusResult.totals?.appointments_count} appts
                  </div>
                </Card>
                <Card className="p-5 rounded-md border border-zinc-200 shadow-none bg-white">
                  <div className="text-2xs uppercase tracking-[0.2em] text-zinc-500">
                    Total profit
                  </div>
                  <div className="font-mono-num text-2xl text-zinc-950 mt-2">
                    {fmtMoney(bonusResult.totals?.profit)}
                  </div>
                  <div className="text-xs text-zinc-500 font-mono-num mt-1">
                    after pay {fmtMoney(bonusResult.totals?.pay)} + super{" "}
                    {fmtMoney(bonusResult.totals?.super)}
                  </div>
                </Card>
              </div>

              <Card className="rounded-md border border-zinc-200 shadow-none overflow-hidden">
                <div className="overflow-x-auto thin-scroll">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                        {[
                          "Pay period",
                          "Appts",
                          "Rev. ex GST",
                          "Pay",
                          "Super",
                          "Profit",
                          "Opportunity",
                          "Bonus",
                        ].map((l) => (
                          <TableHead
                            key={l}
                            className="text-xs font-semibold uppercase tracking-wider text-zinc-600"
                          >
                            {l}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(bonusResult.periods || []).map((p) => (
                        <TableRow
                          key={p.payslip_id}
                          data-testid={`bonus-period-${p.payslip_id}`}
                        >
                          <TableCell className="font-mono-num text-xs text-zinc-700">
                            {p.pay_period_start} → {p.pay_period_end}
                          </TableCell>
                          <TableCell className="font-mono-num text-sm">
                            {p.appointments_count}
                          </TableCell>
                          <TableCell className="font-mono-num text-sm text-zinc-950">
                            {fmtMoney(p.revenue_ex_gst)}
                          </TableCell>
                          <TableCell className="font-mono-num text-sm text-zinc-700">
                            {fmtMoney(p.pay)}
                          </TableCell>
                          <TableCell className="font-mono-num text-sm text-zinc-700">
                            {fmtMoney(p.super)}
                          </TableCell>
                          <TableCell
                            className={cn(
                              "font-mono-num text-sm",
                              p.profit < 0 ? "text-red-700" : "text-zinc-950"
                            )}
                          >
                            {fmtMoney(p.profit)}
                          </TableCell>
                          <TableCell
                            className={cn(
                              "font-mono-num text-sm",
                              p.bonus_opportunity > 0
                                ? "text-emerald-700"
                                : "text-zinc-500"
                            )}
                          >
                            {fmtMoney(p.bonus_opportunity)}
                          </TableCell>
                          <TableCell className="font-mono-num text-sm font-medium text-zinc-950">
                            <div className="flex items-center gap-1.5">
                              {fmtMoney(p.bonus)}
                              {p.below_threshold && (
                                <span
                                  className="text-2xs px-1.5 py-0 rounded border border-amber-200 bg-amber-50 text-amber-700"
                                  title={`Calculated bonus ${fmtMoney(p.bonus_raw)} is below the ${fmtMoney(bonusResult.min_payment ?? 20)} minimum, so $0 is paid for this period.`}
                                >
                                  &lt; min
                                </span>
                              )}
                            </div>
                            {p.below_threshold && (
                              <div className="text-2xs text-zinc-400 mt-0.5 font-mono-num">
                                raw {fmtMoney(p.bonus_raw)}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-zinc-100 hover:bg-zinc-100 font-medium">
                        <TableCell className="text-xs uppercase tracking-wider text-zinc-700">
                          Totals
                        </TableCell>
                        <TableCell className="font-mono-num text-sm">
                          {bonusResult.totals?.appointments_count}
                        </TableCell>
                        <TableCell className="font-mono-num text-sm">
                          {fmtMoney(bonusResult.totals?.revenue_ex_gst)}
                        </TableCell>
                        <TableCell className="font-mono-num text-sm">
                          {fmtMoney(bonusResult.totals?.pay)}
                        </TableCell>
                        <TableCell className="font-mono-num text-sm">
                          {fmtMoney(bonusResult.totals?.super)}
                        </TableCell>
                        <TableCell className="font-mono-num text-sm">
                          {fmtMoney(bonusResult.totals?.profit)}
                        </TableCell>
                        <TableCell className="font-mono-num text-sm">
                          {fmtMoney(bonusResult.totals?.bonus_opportunity)}
                        </TableCell>
                        <TableCell className="font-mono-num text-sm">
                          {fmtMoney(bonusResult.totals?.bonus)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </Card>

              {bonusResult.note && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                  {bonusResult.note}
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Employees */}
      <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
          <LinkIcon className="h-3.5 w-3.5 text-zinc-500" />
          <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Employees · Calendar links
          </h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Employee
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Slips
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Range
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Linked calendar
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {employees.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-12">
                  <div className="flex flex-col items-center justify-center text-center text-zinc-500">
                    <Inbox className="h-7 w-7 mb-2 text-zinc-300" />
                    <div className="text-sm">No payslips yet</div>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {employees.map((e) => (
              <TableRow key={e.employee_name} data-testid={`employee-row-${e.employee_name}`}>
                <TableCell>
                  <div className="font-medium text-zinc-950">{e.employee_name}</div>
                  <div className="text-xs text-zinc-500 font-mono-num">
                    {fmtMoney(e.gross_total)} gross · {e.hours_total.toFixed(1)} h
                  </div>
                </TableCell>
                <TableCell className="font-mono-num">{e.slips}</TableCell>
                <TableCell className="font-mono-num text-xs text-zinc-600">
                  {e.earliest} → {e.latest}
                </TableCell>
                <TableCell>
                  <Select
                    value={e.linked_calendar_id ? String(e.linked_calendar_id) : ""}
                    onValueChange={(v) =>
                      linkEmployeeCalendar(e.employee_name, v ? Number(v) : null)
                    }
                  >
                    <SelectTrigger
                      className="border-zinc-300 w-64"
                      data-testid={`link-cal-${e.employee_name}`}
                    >
                      <SelectValue placeholder="Pick a calendar" />
                    </SelectTrigger>
                    <SelectContent>
                      {calendars.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* All slips */}
      <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 text-zinc-500" />
          <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            All payslips
          </h3>
        </div>
        <div className="overflow-x-auto thin-scroll">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                {["Period", "Employee", "Hours", "Gross", "Tax", "Super", "Net", "Bonus", "Actions"].map(
                  (l) => (
                    <TableHead
                      key={l}
                      className="text-xs font-semibold uppercase tracking-wider text-zinc-600"
                    >
                      {l}
                    </TableHead>
                  )
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {slips.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-12">
                    <div className="flex flex-col items-center justify-center text-center text-zinc-500">
                      <Inbox className="h-7 w-7 mb-2 text-zinc-300" />
                      <div className="text-sm">No slips uploaded yet</div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {slips.map((s) => (
                <TableRow key={s.id} data-testid={`slip-row-${s.id}`}>
                  <TableCell className="font-mono-num text-xs text-zinc-700">
                    {s.pay_period_start} → {s.pay_period_end}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-zinc-950">{s.employee_name}</span>
                      {s.source === "xero" && (
                        <Badge
                          variant="outline"
                          className="text-2xs border-sky-200 bg-sky-50 text-sky-700"
                          title="Synced from Xero Payroll"
                        >
                          xero
                        </Badge>
                      )}
                    </div>
                    {s.linked_calendar_id ? (
                      <Badge
                        variant="outline"
                        className="text-2xs border-emerald-200 bg-emerald-50 text-emerald-700"
                      >
                        cal #{s.linked_calendar_id}
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-2xs border-amber-200 bg-amber-50 text-amber-700"
                      >
                        unlinked
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono-num">
                    {Number(s.hours_total || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="font-mono-num">{fmtMoney(s.gross_pay)}</TableCell>
                  <TableCell className="font-mono-num">{fmtMoney(s.tax)}</TableCell>
                  <TableCell className="font-mono-num">{fmtMoney(s.super)}</TableCell>
                  <TableCell className="font-mono-num">{fmtMoney(s.net_pay)}</TableCell>
                  <TableCell data-testid={`slip-bonus-${s.id}`}>
                    {bonusBySlip[s.id] ? (
                      bonusBySlip[s.id].below_threshold ? (
                        <Badge
                          variant="outline"
                          className="text-2xs border-zinc-200 bg-zinc-50 text-zinc-600"
                          title={`Calculated ${bonusBySlip[s.id].calculated_at?.slice(0, 10)} — below profit threshold`}
                        >
                          No bonus
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-2xs border-emerald-200 bg-emerald-50 text-emerald-800 font-mono-num"
                          title={`Calculated ${bonusBySlip[s.id].calculated_at?.slice(0, 10)} · raw $${Number(bonusBySlip[s.id].bonus_raw || 0).toFixed(2)} · rate ${bonusBySlip[s.id].rate}`}
                        >
                          Bonus {fmtMoney(bonusBySlip[s.id].bonus)}
                        </Badge>
                      )
                    ) : (
                      <span className="text-2xs text-zinc-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-zinc-300 hover:bg-zinc-100 h-8 px-2"
                        onClick={() => setSelected(s)}
                        data-testid={`view-slip-${s.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {s.pdf_id && (
                        <Button
                          asChild
                          size="sm"
                          variant="outline"
                          className="border-zinc-300 hover:bg-zinc-100 h-8 px-2"
                        >
                          <a
                            href={`${api.defaults.baseURL}/payslips/pdf/${s.pdf_id}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`download-slip-${s.id}`}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-200 text-red-700 hover:bg-red-50 h-8 px-2"
                        onClick={() => deleteSlip(s.id)}
                        data-testid={`delete-slip-${s.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Slip detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">
              {selected?.employee_name}
            </DialogTitle>
            <DialogDescription className="font-mono-num text-xs">
              {selected?.pay_period_start} → {selected?.pay_period_end} · paid{" "}
              {selected?.payment_date}
            </DialogDescription>
          </DialogHeader>
          <pre className="text-xs font-mono-num bg-zinc-950 text-zinc-100 p-4 rounded-md overflow-auto max-h-[60vh]">
            {JSON.stringify(selected, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>

      {!status?.configured && (
        <p className="text-sm text-zinc-500 text-center">
          Tip: <Link to="/settings" className="underline">connect Acuity</Link> to
          enable calendar linking and bonus calculation.
        </p>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-zinc-500 whitespace-pre">{k}</span>
      <span className="font-mono-num text-zinc-950">{v}</span>
    </div>
  );
}
